import { domainError, type DomainError } from '../shared/domain-error';
import { err, ok, type Result } from '../shared/result';
import { PLANNING_ERROR, PLANNING_ERROR_MESSAGE } from './errors';

/**
 * O tempo e a jornada, turno a turno (Fase 18, fatia 2).
 *
 * ```text
 * Turno/ Tempo
 * Manhã: 07:00 a 12:00   Nublado
 * Tarde: 13:00 a 17:00   Nublado
 * Noite:
 * ```
 *
 * ## Por que são dois campos, e não um
 *
 * A tentação é uma lista só — `Bom, Nublado, Chuvoso, Impraticável` — e ela perde a
 * informação que o documento existe para carregar. O RDO sustenta pedido de prorrogação
 * por chuva, e para isso **"choveu e trabalhou" e "choveu e parou" são dias diferentes**.
 * Numa lista única os dois viram a mesma linha, ou o `Chuvoso` some no dia em que foi
 * impraticável — e some justamente o fato que dá causa ao pedido.
 *
 * Então `condition` é o que o céu fez, e `workable` é o que isso custou. Contar as duas
 * coisas é o trabalho do documento; deduzir uma da outra seria inventá-la.
 *
 * ## Por que o turno não atravessa a meia-noite
 *
 * `endTime` é sempre maior que `startTime`, o que impede um turno de 22:00 às 06:00. Não é
 * limitação: o RDO é o relatório **de um dia**, e o que se trabalhou depois da meia-noite
 * aconteceu no dia seguinte e pertence ao RDO do dia seguinte. Permitir a travessia faria
 * as mesmas horas caberem em dois documentos, e ninguém saberia em qual delas somar.
 */

export type ReportShift = 'MORNING' | 'AFTERNOON' | 'NIGHT';

/** A ordem em que os três saem no papel, e a única ordem que existe. */
export const REPORT_SHIFTS: readonly ReportShift[] = ['MORNING', 'AFTERNOON', 'NIGHT'];

export const REPORT_SHIFT_LABEL: Readonly<Record<ReportShift, string>> = {
  MORNING: 'Manhã',
  AFTERNOON: 'Tarde',
  NIGHT: 'Noite',
};

/**
 * O que o céu fez.
 *
 * Lista fechada, e curta de propósito: quem preenche está no canteiro com o tablet na mão
 * no fim do dia, e vinte opções viram a primeira da lista clicada sem ler. O vocabulário
 * mora aqui, num lugar só — trocá-lo é editar este mapa e nada mais.
 */
export type WeatherCondition = 'CLEAR' | 'CLOUDY' | 'RAINY' | 'STORM';

export const WEATHER_CONDITIONS: readonly WeatherCondition[] = [
  'CLEAR',
  'CLOUDY',
  'RAINY',
  'STORM',
];

/*
 * O vocabulário é o do cliente, dado em 25/09/2026: limpo, nublado, chuva, tempestade.
 *
 * A tempestade não é "muita chuva": é o dia em que o guincho para e a concretagem não sai,
 * e quem lê o RDO um mês depois precisa distinguir uma coisa da outra para entender o
 * atraso. Por isso ela é uma quarta opção, e não um advérbio na observação.
 */
export const WEATHER_CONDITION_LABEL: Readonly<Record<WeatherCondition, string>> = {
  CLEAR: 'Limpo',
  CLOUDY: 'Nublado',
  RAINY: 'Chuva',
  STORM: 'Tempestade',
};

export interface ShiftWeather {
  readonly shift: ReportShift;
  /** `HH:MM`. Anda em par com `endTime`: meia jornada declarada não é jornada. */
  readonly startTime?: string;
  readonly endTime?: string;
  readonly condition?: WeatherCondition;
  /**
   * Deu para trabalhar.
   *
   * Verdadeiro é o dia comum, e é por isso que ele é o padrão. Falso é o que sustenta o
   * pedido de prorrogação, e por isso exige dizer o que impediu (RN-215).
   */
  readonly workable: boolean;
}

const HORA = /^([01]\d|2[0-3]):([0-5]\d)$/;

function minutos(hora: string): number {
  const partes = HORA.exec(hora);
  if (partes === null) return Number.NaN;
  return Number(partes[1]) * 60 + Number(partes[2]);
}

/**
 * O turno foi relatado — tem hora, ou tem tempo, ou diz que não deu para trabalhar.
 *
 * É a RN-211: **turno em branco não é impresso**. No RDO de referência a noite está vazia,
 * e imprimir "Noite: — —" diria que alguém olhou e não encontrou nada, quando a verdade é
 * que não se trabalha à noite naquela obra.
 */
export function isShiftReported(shift: ShiftWeather): boolean {
  return (
    shift.startTime !== undefined ||
    shift.endTime !== undefined ||
    shift.condition !== undefined ||
    !shift.workable
  );
}

/** Só os turnos que têm o que dizer, na ordem do papel. */
export function reportedShifts(shifts: readonly ShiftWeather[]): readonly ShiftWeather[] {
  const porTurno = new Map(shifts.map((shift) => [shift.shift, shift]));
  return REPORT_SHIFTS.map((turno) => porTurno.get(turno)).filter(
    (shift): shift is ShiftWeather => shift !== undefined && isShiftReported(shift),
  );
}

function refuse(code: string, details?: Record<string, unknown>): Result<never, DomainError> {
  return err(domainError(code, PLANNING_ERROR_MESSAGE[code]!, details));
}

/**
 * Valida e normaliza os turnos que a tela mandou.
 *
 * Devolve **só os relatados**, na ordem do papel: guardar três linhas em branco para
 * representar "nada a dizer" encheria o banco de ausência, e a leitura teria de filtrá-las
 * de novo em todo lugar que as tocasse.
 */
export function parseShifts(
  entradas: readonly ShiftWeather[],
): Result<readonly ShiftWeather[], DomainError> {
  const vistos = new Set<ReportShift>();

  for (const turno of entradas) {
    if (vistos.has(turno.shift)) {
      return refuse(PLANNING_ERROR.SHIFT_DUPLICATED, { shift: turno.shift });
    }
    vistos.add(turno.shift);

    const temInicio = turno.startTime !== undefined;
    const temFim = turno.endTime !== undefined;

    // Meia jornada declarada não é jornada: "começou às 7" sem dizer até quando não
    // sustenta hora nenhuma, e sairia no papel parecendo informação.
    if (temInicio !== temFim) {
      return refuse(PLANNING_ERROR.SHIFT_TIME_INCOMPLETE, { shift: turno.shift });
    }

    if (temInicio && temFim) {
      const inicio = minutos(turno.startTime);
      const fim = minutos(turno.endTime);
      if (Number.isNaN(inicio) || Number.isNaN(fim)) {
        return refuse(PLANNING_ERROR.SHIFT_TIME_INVALID, {
          shift: turno.shift,
          startTime: turno.startTime,
          endTime: turno.endTime,
        });
      }
      if (fim <= inicio) {
        return refuse(PLANNING_ERROR.SHIFT_TIME_NOT_ORDERED, {
          shift: turno.shift,
          startTime: turno.startTime,
          endTime: turno.endTime,
        });
      }
    }

    // RN-215 — dizer que não deu para trabalhar sem dizer o que impediu é a metade da
    // frase que não serve para nada: é justamente a outra metade que sustenta o pedido.
    if (!turno.workable && turno.condition === undefined) {
      return refuse(PLANNING_ERROR.SHIFT_BLOCKED_WITHOUT_REASON, { shift: turno.shift });
    }
  }

  return ok(reportedShifts(entradas));
}

/** `07:00 a 12:00 · Nublado` — a linha do turno, como ela sai no papel. */
export function describeShift(shift: ShiftWeather): string {
  const partes: string[] = [];
  if (shift.startTime !== undefined && shift.endTime !== undefined) {
    partes.push(`${shift.startTime} a ${shift.endTime}`);
  }
  if (shift.condition !== undefined) partes.push(WEATHER_CONDITION_LABEL[shift.condition]);
  if (!shift.workable) partes.push('Impraticável');
  return partes.join(' · ');
}
